import { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotifications } from 'app/application/application'
import { Form } from 'app/common/presentation'
import { formatDate } from 'app/common/presentation/format'
import { useUser } from 'app/user/application'
import { validatePassword } from 'app/user/presentation/validatePassword'
import { ActiveUserWorkspace, InvitedUserWorkspace } from 'app/user/types'

import { Button, Modal, Status, Table } from 'components'

import styles from './User.module.scss'

const UserSettings = () => {
  const { t } = useTranslation('settings')
  const { createNotification, te } = useNotifications()
  const { handleInvite, update, user } = useUser()

  const [firstName] = useState(user?.firstName ?? '')
  const [lastName] = useState(user?.lastName ?? '')
  const [email] = useState(user?.email ?? '')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const workspaces = user?.workspaces ?? []
  const activeWorkspaces = workspaces.filter(
    (workspace): workspace is ActiveUserWorkspace => workspace.status === 'active',
  )
  const invitedWorkspaces = workspaces.filter(
    (workspace): workspace is InvitedUserWorkspace => workspace.status === 'invited',
  )

  useEffect(() => {
    if (!showPasswordModal) {
      setNewPassword('')
      setNewPasswordRepeat('')
    }
  }, [showPasswordModal])

  const handlePasswordChange = (e: SyntheticEvent) => {
    e.preventDefault()

    const passwordError = validatePassword(newPassword, newPasswordRepeat)
    if (passwordError) {
      setPasswordError(te(`password.${passwordError}`))
    } else {
      setPasswordError('')
      void update(firstName, lastName, newPassword).then((result) => {
        if (result.ok) {
          setShowPasswordModal(false)
          createNotification({
            message: t('user.passwordChange.success.message'),
            title: t('user.passwordChange.success.title'),
            type: 'success',
          })
        } else {
          setPasswordError(result.val.message)
        }
      })
    }
  }

  return (
    <div className={styles.user}>
      <div>
        <Form.InputFormRow title={t('user.firstName.title')} value={firstName} />
        <Form.InputFormRow title={t('user.lastName.title')} value={lastName} />
        <Form.InputFormRow title={t('user.email.title')} value={email} />
        <Form.ActionFormRow
          actionTitle={t('user.password.change')}
          onClick={() => setShowPasswordModal(true)}
          title={t('user.password.title')}
        />
      </div>

      <div className={styles.workspaces}>
        <div className={styles.header}>
          <div className={styles.title}>{t('user.workspaces.title')}</div>
          <div className={styles.description}>{t('user.workspaces.description')}</div>
        </div>
        <Table.Table
          columns={[
            t('user.workspaces.active.table.name'),
            t('user.workspaces.active.table.role'),
            t('user.workspaces.active.table.status'),
          ]}
          data={activeWorkspaces}
          renderRow={(activeWorkspace, index) => (
            <Table.Row key={index}>
              <Table.Cell>{activeWorkspace.name}</Table.Cell>
              <Table.Cell>
                <Status status={activeWorkspace.isAdmin ? 'purple' : 'normal'}>
                  {t(`user.workspaces.active.role.${activeWorkspace.isAdmin ? 'admin' : 'member'}`)}
                </Status>
              </Table.Cell>
              <Table.Cell>
                {activeWorkspace.joined
                  ? t('user.workspaces.active.joined', {
                      date: formatDate(activeWorkspace.joined),
                    })
                  : ''}
              </Table.Cell>
            </Table.Row>
          )}
        />
        {invitedWorkspaces.length > 0 && (
          <Table.Table
            className={styles.pending}
            columns={[t('user.workspaces.pending.table.pending'), '']}
            data={invitedWorkspaces}
            renderRow={(invitedWorkspace, index) => (
              <Table.Row key={index}>
                <Table.Cell>{invitedWorkspace.name}</Table.Cell>
                <Table.Cell>
                  <Button
                    onClick={user ? () => void handleInvite('accept', invitedWorkspace) : undefined}
                    small
                  >
                    {t('user.workspaces.pending.accept')}
                  </Button>
                  <Button
                    destructive
                    onClick={
                      user ? () => void handleInvite('decline', invitedWorkspace) : undefined
                    }
                    small
                    variant="secondary"
                  >
                    {t('user.workspaces.pending.decline')}
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
          />
        )}
      </div>

      <Modal
        actions={
          <>
            <Button onClick={() => setShowPasswordModal(false)} variant="secondary">
              {t('user.passwordChange.actions.cancel')}
            </Button>
            <Button onClick={handlePasswordChange} type="submit" variant="primary-gray">
              {t('user.passwordChange.actions.submit')}
            </Button>
          </>
        }
        className={styles.modal}
        contentClassName={styles.content}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordChange}
        open={showPasswordModal}
        title={t('user.passwordChange.title')}
      >
        <div>{t('user.passwordChange.description')}</div>
        <input autoComplete="username" hidden name="username" readOnly type="email" value={email} />
        <Form.InputFormRow
          autoComplete="current-password"
          autoFocus
          error={passwordError ? ' ' : undefined}
          mode="column"
          onChange={setNewPassword}
          placeholder={t('user.passwordChange.placeholder')}
          title={t('user.passwordChange.password')}
          type="password"
          value={newPassword}
        />
        <Form.InputFormRow
          autoComplete="new-password"
          error={passwordError}
          mode="column"
          onChange={setNewPasswordRepeat}
          placeholder={t('user.passwordChange.placeholder')}
          title={t('user.passwordChange.passwordRepeat')}
          type="password"
          value={newPasswordRepeat}
        />
      </Modal>
    </div>
  )
}

export default UserSettings
